import { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { getAgencyAgentsDropdown, staffMandateService } from "../../services/api";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
  Modal,
  Sheet,
  Button,
  SearchInput,
  FieldLabel,
  PickList,
  PickRow,
  PersonAvatar,
  InfoBox,
  InfoBoxList,
  TransferArrowsIcon,
  ArchiveBoxIcon,
} from "./v2";
import FifaVerifiedBadge, {
  isFifaVerified,
} from "../common/FifaVerifiedBadge";

// TP-Coach-Mandate — staff variant of ChangeAgentModal (Brief §4.6),
// My Agency V2 restyle (template = players boards 2d desktop modal /
// 2e mobile sheet; the staff surface has no artboards of its own). The
// agent-picker mechanic (getAgencyAgentsDropdown source, current-agent
// exclusion, searchable list with match highlight, confirm disabled until
// chosen) matches the players modal; ONLY the submit target differs:
// POST /users/agency/staff/{id}/reassign-agent/ transfers the coach/staff
// mandate. ALL LOGIC IS UNCHANGED (Rule 7 — STAFF-INVENTORY.md §5):
// staffId = board row `id`, kind ('staff'|'coach') required (error toast
// if missing), NO legacy soft-create redirect (player-only path), success
// → onSuccess → staff board refetch. The player ChangeAgentModal stays
// byte-identical (clone, don't edit).

// 2d search-match highlight — mark runs across the full name
// (dc.html verbatim: background #FFF1B8, radius 3, padding 0 1px).
const highlightMatch = (name, query) => {
  const q = (query || "").trim();
  if (!q) return name;
  const idx = name.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return name;
  return (
    <>
      {name.slice(0, idx)}
      <mark style={{ background: "#FFF1B8", borderRadius: 3, padding: "0 1px", color: "inherit" }}>
        {name.slice(idx, idx + q.length)}
      </mark>
      {name.slice(idx + q.length)}
    </>
  );
};

const ChangeStaffAgentModal = ({ isOpen, onClose, member, onSuccess }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingAgents, setFetchingAgents] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAgents();
    }
  }, [isOpen]);

  const fetchAgents = async () => {
    setFetchingAgents(true);
    try {
      // Dropdown source: {account_id, name, avatar_url, player_count, is_licensed}.
      const response = await getAgencyAgentsDropdown();
      const list = response.data?.results || response.data || [];
      setAgents(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to fetch agents", error);
      toast.error("Failed to load agents");
    } finally {
      setFetchingAgents(false);
    }
  };

  const currentAgentAccountId = member?.current_agent_account_id || member?.managing_agent?.account_id || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return agents
      // Exclude the member's current agent from the target list.
      .filter((a) => !currentAgentAccountId || String(a.account_id) !== String(currentAgentAccountId))
      .filter((a) => !q || (a.name || "").toLowerCase().includes(q));
  }, [agents, query, currentAgentAccountId]);

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();

    if (!selectedAgent) {
      toast.error("Please select an agent");
      return;
    }

    setLoading(true);
    try {
      // BE contract: <member_id> is the Staff/Coach PK (the staff board row
      // `id`), and the body requires kind ('staff'|'coach') + the new agent's
      // ACCOUNT id — the board row carries both.
      const staffId = member?.id || member?.staff_id;
      const kind = member?.kind;

      if (!staffId || !kind) {
        toast.error("Staff member ID not found");
        setLoading(false);
        return;
      }

      const result = await staffMandateService.reassignStaffAgent(
        staffId,
        parseInt(selectedAgent, 10),
        kind
      );

      if (result.success) {
        toast.success(result.message || "Staff member reassigned successfully");
        onSuccess();
        handleClose();
      } else {
        toast.error(result.error || "Failed to reassign staff member");
      }
    } catch (error) {
      console.error("Error reassigning staff member:", error);
      toast.error("An error occurred while reassigning the staff member");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedAgent("");
      setQuery("");
      onClose();
    }
  };

  if (!isOpen) return null;

  const currentAgentName = member?.managing_agent?.name || null;
  const memberFirstName = member?.name ? member.name.split(" ")[0] : null;
  const selectedAgentName =
    agents.find((a) => String(a.account_id || a.id) === String(selectedAgent))?.name || null;
  // Consequence copy (2d/2e template): real names from live state with
  // neutral fallbacks before a selection exists — never hardcoded samples.
  const newAgentLabel = selectedAgentName || "the new agent";
  const previousAgentLabel = currentAgentName || "the previous agent";

  const pickRows = fetchingAgents ? (
    <div className="text-center py-6 text-[13px] text-[#64748B]">Loading agents…</div>
  ) : filtered.length === 0 ? (
    <div className="text-center py-6 text-[13px] text-[#64748B]">No agents found</div>
  ) : (
    filtered.map((a, i) => {
      const id = a.account_id || a.id;
      const name = a.name || "Agent";
      const count = a.player_count ?? a.active_player_slots ?? 0;
      // Badge rule (Marin 2026-08-04): isFifaVerified — self-declared
      // licence (is_licensed here; the dropdown payload has no number)
      // OR fifa_verified, hidden only on failed_qr.
      const verified = isFifaVerified(a);
      return (
        <PickRow
          key={id}
          first={i === 0}
          selected={String(selectedAgent) === String(id)}
          onClick={() => setSelectedAgent(String(id))}
          mobile={isMobile}
          avatar={
            <PersonAvatar
              src={a.avatar_url || a.profile_picture_url || null}
              name={name}
              seed={id}
              size={34}
            />
          }
          name={highlightMatch(name, query)}
          // 2e: no license sub-lines on mobile.
          subline={
            !isMobile && verified ? <FifaVerifiedBadge size="sm" /> : null
          }
          rightMeta={`${count} player${count === 1 ? "" : "s"}`}
        />
      );
    })
  );

  // ── Mobile: 2e-template bottom sheet ─────────────────────────────────
  if (isMobile) {
    return (
      <Sheet
        open={isOpen}
        onClose={handleClose}
        title="Change managing agent"
        top={96}
        closeLabel="Close"
        footer={
          <Button
            mobile
            disabled={loading || fetchingAgents || !selectedAgent}
            onClick={handleSubmit}
          >
            {loading ? "Changing agent…" : "Change agent"}
          </Button>
        }
      >
        {/* 2e subtitle shortens "currently managed by" → "currently" */}
        <div className="text-[13px] text-[#64748B]">
          {member?.name}
          {currentAgentName ? (
            <>
              {" "}· currently <b className="text-[#0F172A] font-semibold">{currentAgentName}</b>
            </>
          ) : null}
        </div>
        <div className="mt-4">
          <SearchInput
            mobile
            width="100%"
            value={query}
            onChange={setQuery}
            placeholder="Search agents"
            inputProps={{ autoFocus: true }}
          />
        </div>
        <PickList style={{ marginTop: 10 }}>{pickRows}</PickList>
        {/* 2e consequence boxes: single condensed paragraphs */}
        <div
          className="rounded-[11px] border border-[#D6E7FF] bg-[#F5F9FF]"
          style={{ padding: "12px 14px", marginTop: 12 }}
        >
          <div className="text-[12px] font-bold text-[#1668D9]">What this changes</div>
          <p className="text-[12.5px] text-[#334155] leading-[1.5]" style={{ margin: "5px 0 0" }}>
            Representation (countries, end date, exclusivity) transfers to {newAgentLabel};{" "}
            {memberFirstName ? `${memberFirstName}’s staff profile` : "the staff member’s profile"} updates.
          </p>
        </div>
        <div
          className="rounded-[11px] border border-[#EDF1F6] bg-[#F8FAFC]"
          style={{ padding: "12px 14px", marginTop: 8 }}
        >
          <div className="text-[12px] font-bold text-[#64748B]">
            What stays with {previousAgentLabel}
          </div>
          <p className="text-[12.5px] text-[#334155] leading-[1.5]" style={{ margin: "5px 0 0" }}>
            Deal-tracker entries, sent pitches, notes and chats. Documents stay accessible.
          </p>
        </div>
      </Sheet>
    );
  }

  // ── Desktop: 2d-template modal (520px, footnote footer) ──────────────
  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      width={520}
      title="Change managing agent"
      subtitle={
        <>
          {member?.name}
          {currentAgentName ? (
            <>
              {" "}· currently managed by{" "}
              <b className="text-[#0F172A] font-semibold">{currentAgentName}</b>
            </>
          ) : null}
        </>
      }
      closeLabel="Close"
      footnote="Confirm activates once an agent is selected."
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || fetchingAgents || !selectedAgent}
          >
            {loading ? "Changing agent…" : "Change agent"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2" style={{ marginTop: 18 }}>
        <FieldLabel>New agent</FieldLabel>
        <SearchInput
          width="100%"
          value={query}
          onChange={setQuery}
          placeholder="Search agents…"
          inputProps={{ autoFocus: true }}
        />
      </div>
      <PickList style={{ maxHeight: 220, overflowY: "auto" }}>{pickRows}</PickList>
      {/* Consequence panels (behavior unchanged, laid out per 2d) */}
      <InfoBox
        tone="blue"
        icon={<TransferArrowsIcon />}
        header="What this changes"
        style={{ marginTop: 16 }}
      >
        <InfoBoxList>
          <li>
            The representation (countries, end date, exclusivity) transfers to{" "}
            <b className="font-semibold text-[#0F172A]">{newAgentLabel}</b>
          </li>
          <li>
            {memberFirstName ? `${memberFirstName}’s staff profile` : "The staff member’s profile"} shows{" "}
            {newAgentLabel} as managing agent
          </li>
        </InfoBoxList>
      </InfoBox>
      <InfoBox
        tone="grey"
        icon={<ArchiveBoxIcon />}
        header={`What stays with ${previousAgentLabel}`}
        style={{ marginTop: 10 }}
      >
        <InfoBoxList>
          <li>Deal-tracker entries and sent pitches</li>
          <li>Notes and chat history</li>
          <li>Uploaded documents stay accessible to the agency</li>
        </InfoBoxList>
      </InfoBox>
    </Modal>
  );
};

export default ChangeStaffAgentModal;
